import { Search, Download, Visibility, CheckCircle, Payment } from '@mui/icons-material'
import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, FormControl, Grid, MenuItem, Pagination, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, InputAdornment } from '@mui/material';
import { styled } from '@mui/system'
import "./monthlyPayments.css"
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader/Loader'
import Toptag from '../../components/toptag/Toptag'
import apiFunctions from '../../api/apiFunctions'
import { formatMonDayYear } from '../../utils/formatDate';
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "box-shadow 0.3s ease",
    '&:hover': {
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
    }
}))

const FilterPaper = styled(Paper)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "#f8f9fa",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    marginBottom: "20px"
}))

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "white",
    maxHeight: "calc(100vh - 300px)",
    '& .MuiTableHead-root': {
        backgroundColor: "#f5f7fa",
        '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: "14px",
            color: "#374151",
            borderBottom: "2px solid #e5e7eb"
        }
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: "background-color 0.2s ease",
            '&:hover': {
                backgroundColor: "#f9fafb"
            },
            '&:nth-of-type(even)': {
                backgroundColor: "#fafbfc"
            }
        },
        '& .MuiTableCell-root': {
            fontSize: "14px",
            color: "#4b5563",
            borderBottom: "1px solid #e5e7eb",
            padding: "16px"
        }
    }
}))

const StatCard = styled(Paper)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    textAlign: "center",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    '&:hover': {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
    }
}))

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function MonthlyPayments() {
    const [all, setAll] = useState([]);
    const [isLoading, setIsloading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const navigate = useNavigate();
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1)
    const [searchKey, setSearchKey] = useState("");
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [status, setStatus] = useState("");

    const searchHandle = (event) => {
        const newQuery = event.target.value;
        setSearchKey(newQuery)
        setPage(1);
    }

    const getAll = async () => {
        setIsloading(true);
        try {
            let result = await apiFunctions.getAllMonthlyPayments(limit, searchKey, page, year, month, status);
            if (!result.data.status) {
                if (result.data.code === 401) {
                    localStorage.clear();
                    navigate("/login")
                    toast.info("Session Expired Please login again!!")
                }
                toast.error(result.data.message, {
                    position: toast.POSITION.TOP_RIGHT
                });
            } else {
                setAll(result.data.data || []);
                setTotalCount(result.data.total || 0)
            }
        } catch (error) {
            toast.error("Error fetching monthly payments", {
                position: toast.POSITION.TOP_RIGHT
            });
        } finally {
            setIsloading(false)
        }
    }

    useEffect(() => {
        getAll()
    }, [searchKey, limit, page, year, month, status])

    const handleChange = (e) => {
        setLimit(e.target.value)
        setPage(1)
    };

    const skipPagination = (e, newPage) => {
        setPage(newPage)
    }

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return formatMonDayYear(dateString);
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'success';
            case 'approved':
                return 'info';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    }

    const handleViewDetail = (id) => {
        navigate(`/home/monthly-payouts/detail?id=${id}`);
    }

    const handleApprove = async (id) => {
        try {
            let result = await apiFunctions.approveMonthlyPayment(id);
            if (result.data.status) {
                toast.success("Monthly payment approved successfully", {
                    position: toast.POSITION.TOP_RIGHT
                });
                getAll();
            } else {
                toast.error(result.data.message || "Failed to approve payment", {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        } catch (error) {
            toast.error("Error approving payment", {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }

    const handleConfirmPayment = async (id) => {
        try {
            let result = await apiFunctions.confirmMonthlyPayment(id);
            if (result.data.status) {
                toast.success("Payment confirmed successfully", {
                    position: toast.POSITION.TOP_RIGHT
                });
                getAll();
            } else {
                toast.error(result.data.message || "Failed to confirm payment", {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        } catch (error) {
            toast.error("Error confirming payment", {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }

    const handleExport = async () => {
        try {
            const response = await apiFunctions.exportMonthlyPaymentsExcel(year, month);
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const monthName = monthNames[month - 1];
            link.setAttribute('download', `monthly_payments_${year}_${monthName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Excel file downloaded successfully", {
                position: toast.POSITION.TOP_RIGHT
            });
        } catch (error) {
            console.error('Export error:', error);
            toast.error(error.response?.data?.message || "Error exporting Excel file", {
                position: toast.POSITION.TOP_RIGHT
            });
        }
    }

    const handleMarkAllAsPaid = async () => {
        if (!year || !month) {
            toast.warning("Please select both year and month", {
                position: toast.POSITION.TOP_RIGHT
            });
            return;
        }

        const pendingOrApproved = all.filter(p => p.status === 'pending' || p.status === 'approved');
        if (pendingOrApproved.length === 0) {
            toast.info("No pending or approved payments to mark as paid", {
                position: toast.POSITION.TOP_RIGHT
            });
            return;
        }

        const confirmMessage = `Are you sure you want to mark all ${pendingOrApproved.length} pending/approved payment(s) for ${monthNames[month - 1]} ${year} as paid?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setIsloading(true);
            let result = await apiFunctions.markAllMonthlyPaymentsAsPaid(year, month);
            if (result.data.status) {
                toast.success(`Successfully marked ${result.data.data?.modifiedCount || 0} payment(s) as paid`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                getAll();
            } else {
                toast.error(result.data.message || "Failed to mark payments as paid", {
                    position: toast.POSITION.TOP_RIGHT
                });
                setIsloading(false);
            }
        } catch (error) {
            console.error('Mark all as paid error:', error);
            toast.error(error.response?.data?.message || "Error marking payments as paid", {
                position: toast.POSITION.TOP_RIGHT
            });
            setIsloading(false);
        }
    }

    const getYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push(i);
        }
        return years;
    }

    const totalAmount = all.reduce((sum, payment) => sum + (payment.total_usd ?? payment.total_amount ?? 0), 0);
    const pendingCount = all.filter(p => p.status === 'pending').length;
    const approvedCount = all.filter(p => p.status === 'approved').length;
    const paidCount = all.filter(p => p.status === 'paid').length;

    return (
        <Box sx={{ padding: "20px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
            <Toptag name="Monthly Payouts" />
            <Grid container spacing={3} style={{ marginTop: "10px" }}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 2 }}>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#3b82f6", fontWeight: 600, marginBottom: 1 }}>
                                {totalAmount.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Total Payout USD</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#f59e0b", fontWeight: 600, marginBottom: 1 }}>
                                {pendingCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Pending</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#06b6d4", fontWeight: 600, marginBottom: 1 }}>
                                {approvedCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Approved</Typography>
                        </StatCard>
                        <StatCard sx={{ flex: "1 1 200px" }}>
                            <Typography variant="h4" sx={{ color: "#10b981", fontWeight: 600, marginBottom: 1 }}>
                                {paidCount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">Paid</Typography>
                        </StatCard>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <FilterPaper>
                        <Typography variant="h6" sx={{ marginBottom: 3, color: "#1f2937", fontWeight: 600 }}>
                            Filters & Search
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search by name, email, or wallet..."
                                    value={searchKey}
                                    onChange={searchHandle}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search style={{ color: "#6b7280" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        backgroundColor: "white",
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: "8px"
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={year}
                                        onChange={(e) => { setYear(e.target.value); setPage(1); }}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#9ca3af' }}>Year</span>;
                                            }
                                            return selected;
                                        }}
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            '& .MuiSelect-select': {
                                                color: year ? '#1f2937' : '#9ca3af'
                                            }
                                        }}
                                    >
                                        {getYears().map(y => (
                                            <MenuItem key={y} value={y}>{y}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={month}
                                        onChange={(e) => { setMonth(e.target.value); setPage(1); }}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#9ca3af' }}>Month</span>;
                                            }
                                            return monthNames[selected - 1] || 'All Months';
                                        }}
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            '& .MuiSelect-select': {
                                                color: month ? '#1f2937' : '#9ca3af'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Months</MenuItem>
                                        {monthNames.map((m, i) => (
                                            <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={status}
                                        onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#9ca3af' }}>Status</span>;
                                            }
                                            return selected.charAt(0).toUpperCase() + selected.slice(1);
                                        }}
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            '& .MuiSelect-select': {
                                                color: status ? '#1f2937' : '#9ca3af'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="approved">Approved</MenuItem>
                                        <MenuItem value="paid">Paid</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={limit}
                                        onChange={handleChange}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#9ca3af' }}>Per Page</span>;
                                            }
                                            return `${selected} per page`;
                                        }}
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "8px",
                                            '& .MuiSelect-select': {
                                                color: limit ? '#1f2937' : '#9ca3af'
                                            }
                                        }}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: "flex", gap: 2, marginTop: 3, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Payment />}
                                onClick={handleMarkAllAsPaid}
                                disabled={!year || !month || all.filter(p => p.status === 'pending' || p.status === 'approved').length === 0}
                                sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1
                                }}
                            >
                                Mark All as Paid
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Download />}
                                onClick={handleExport}
                                disabled={!year || !month}
                                sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1
                                }}
                            >
                                Export Excel
                            </Button>
                        </Box>
                    </FilterPaper>
                </Grid>

                <Grid item xs={12}>
                    <StyledPaper>
                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                                <Loader />
                            </Box>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ marginBottom: 2, color: "#1f2937", fontWeight: 600 }}>
                                    Monthly Payouts List ({totalCount} total)
                                </Typography>
                                <StyledTableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>User Name</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Main Wallet</TableCell>
                                                <TableCell>Network</TableCell>
                                                <TableCell align="right">Total USD</TableCell>
                                                <TableCell align="center">Rewards</TableCell>
                                                <TableCell>Period</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {all.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={10} align="center" sx={{ padding: 4 }}>
                                                        <Typography variant="body1" color="textSecondary">
                                                            No monthly payments found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                all.map((payment) => {
                                                    const mainWallet = (payment.user_snapshot?.wallets || []).find((w) => w.role === 'main')
                                                        || payment.user_snapshot?.wallets?.[0]
                                                        || {};
                                                    return (
                                                    <TableRow key={payment._id}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {payment.user_snapshot?.user_name || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                                                {payment.user_snapshot?.email || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                                                                {mainWallet.wallet_address ?
                                                                    `${mainWallet.wallet_address.substring(0, 10)}...` : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{mainWallet.network_name || '-'}</TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                                                                ${(payment.total_usd ?? payment.total_amount ?? 0).toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={payment.reward_details?.length || 0}
                                                                size="small"
                                                                sx={{ backgroundColor: "#eff6ff", color: "#2563eb" }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {monthNames[payment.month - 1]} {payment.year}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={payment.status || 'pending'}
                                                                color={getStatusColor(payment.status)}
                                                                size="small"
                                                                sx={{ fontWeight: 500 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatDateTime(payment.createdAt)}</TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<Visibility />}
                                                                    onClick={() => handleViewDetail(payment._id)}
                                                                    sx={{
                                                                        borderRadius: "6px",
                                                                        textTransform: "none",
                                                                        fontSize: "12px"
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                                {payment.status === 'pending' && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        startIcon={<CheckCircle />}
                                                                        onClick={() => handleApprove(payment._id)}
                                                                        sx={{
                                                                            borderRadius: "6px",
                                                                            textTransform: "none",
                                                                            fontSize: "12px"
                                                                        }}
                                                                    >
                                                                        Approve
                                                                    </Button>
                                                                )}
                                                                {payment.status === 'approved' && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="success"
                                                                        startIcon={<Payment />}
                                                                        onClick={() => handleConfirmPayment(payment._id)}
                                                                        sx={{
                                                                            borderRadius: "6px",
                                                                            textTransform: "none",
                                                                            fontSize: "12px"
                                                                        }}
                                                                    >
                                                                        Confirm
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </StyledTableContainer>
                                {totalCount > 0 && (
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3, paddingTop: 2, borderTop: "1px solid #e5e7eb" }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} results
                                        </Typography>
                                        <Pagination
                                            count={Math.ceil(totalCount / limit)}
                                            page={page}
                                            onChange={skipPagination}
                                            color="primary"
                                            shape="rounded"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </StyledPaper>
                </Grid>
            </Grid>
            <ToastContainer />
        </Box>
    )
}
